import { useState, useEffect, useCallback } from 'react';

/**
 * useDebounce — universal debounce hook for any callback.
 *
 * @param {function} callback - Function to debounce
 * @param {number} delay - Debounce delay in ms
 * @returns {function} Debounced callback
 *
 * @example
 * const debouncedSave = useDebounce((value) => saveToServer(value), 500);
 * debouncedSave(inputValue);
 */
const useDebounce = (callback, delay) => {
  const [timer, setTimer] = useState(null);

  const debouncedCallback = useCallback(
    (...args) => {
      if (timer) clearTimeout(timer);
      const newTimer = setTimeout(() => {
        callback(...args);
      }, delay);
      setTimer(newTimer);
    },
    [callback, delay, timer]
  );

  useEffect(() => {
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [timer]);

  return debouncedCallback;
};

export default useDebounce; 